import * as Sentry from '@sentry/react';
import type { ErrorEvent, EventHint } from '@sentry/react';

const FALLBACK_SENTRY_DSN =
  'https://05a9eccaa2b6416d1b9f5245d4307635@o4511846511935488.ingest.de.sentry.io/4511996383920208';
const SENTRY_TRACES_SAMPLE_RATE = 0.1;
const SENTRY_REPLAYS_SESSION_SAMPLE_RATE = 0.1;
const SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE = 1.0;

const IGNORED_HTTP_STATUSES = new Set([400, 401, 403, 404, 422]);
const IGNORED_THIRD_PARTY_SOURCES = ['widgets/platform.js'];
const IGNORED_ERROR_MESSAGE_FRAGMENTS = ['contentWindow.cards'];

const getHttpStatus = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object') {
    return undefined;
  }
  const candidate = error as {
    response?: { status?: unknown };
    status?: unknown;
  };
  const status = candidate.response?.status ?? candidate.status;
  return typeof status === 'number' ? status : undefined;
};

const getErrorMessage = (error: unknown): string => {
  if (typeof error === 'string') {
    return error;
  }
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    return typeof message === 'string' ? message : '';
  }
  return '';
};

const getErrorStack = (error: unknown): string => {
  if (error && typeof error === 'object' && 'stack' in error) {
    const stack = (error as { stack?: unknown }).stack;
    return typeof stack === 'string' ? stack : '';
  }
  return '';
};

export const filterExpectedHttpErrors = (
  event: ErrorEvent,
  hint?: EventHint
): ErrorEvent | null => {
  const originalException = hint?.originalException;

  const status = getHttpStatus(originalException);
  if (status !== undefined && IGNORED_HTTP_STATUSES.has(status)) {
    return null;
  }

  const message = getErrorMessage(originalException);
  if (message && IGNORED_ERROR_MESSAGE_FRAGMENTS.some((fragment) => message.includes(fragment))) {
    return null;
  }

  const stack = getErrorStack(originalException);
  if (stack && IGNORED_THIRD_PARTY_SOURCES.some((source) => stack.includes(source))) {
    return null;
  }

  return event;
};

export const initSentry = (): void => {
  if (!import.meta.env.PROD) {
    return;
  }

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN || FALLBACK_SENTRY_DSN,
    sendDefaultPii: true,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    tracesSampleRate: SENTRY_TRACES_SAMPLE_RATE,
    replaysSessionSampleRate: SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
    replaysOnErrorSampleRate: SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
    beforeSend: filterExpectedHttpErrors,
  });
};
